import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  TextField
} from "@mui/material";
import { getContactTypes } from "../../facilityManagement/data.js";
import EventBus from '../../utils/EventBus.js'
import HubEventNames from '../EventNames.js'

export default function ShareArticleDialog() {

  const [ownershipEnabled, setOwnershipEnabled] = useState(false);
  const [facilityEnabled, setFacilityEnabled] = useState(false);
  const [qseRoleEnabled, setQseRoleEnabled] = useState(false);
  const [subject, setSubject] = useState('')

  /** selected values are OBJECTS */
  const [ownershipContacts, setOwnershipContacts] = useState([]);
  const [facilityContacts, setFacilityContacts] = useState([]);

  const [facilityContactTypes, setFacilityContactTypes] = useState([]);
  const [ownershipContactTypes, setOwnershipContactTypes] = useState([]);

  const [loading, setLoading] = useState(false)

  const [open, setOpen] = useState(false)
  const [payload, setPayload] = useState()

  const openDialog = (payload) => {
    setPayload(payload)
    setSubject(payload.subject)
    setOpen(true)
  }

  const close = (payload) => {
    if (payload?.onClose) payload?.onClose()
    handleClose();
  }


  useEffect(() => {

    EventBus.on(HubEventNames.OPEN_SHARE_ARTICLE_DIALOG, openDialog)
    EventBus.on(HubEventNames.CLOSE_SHARE_ARITCLE_DIALOG, close)

    return () => {
      EventBus.removeListener(HubEventNames.OPEN_SHARE_ARTICLE_DIALOG, openDialog)
      EventBus.removeListener(HubEventNames.CLOSE_SHARE_ARITCLE_DIALOG, close)
    }
  }, [])

  useEffect(() => {
    if (!open) return;

    (async () => {
      const [ownership, facility] = await Promise.all([
        getContactTypes("ownership", false),
        getContactTypes("manufacturer", false)
      ]);

      setOwnershipContactTypes(
        ownership.filter(ct => !ct.isInternal && ct.name !== 'Other')
      );
      setFacilityContactTypes(
        facility.filter(ct => !ct.isInternal && ct.name !== 'Other')
      );
    })();
  }, [open]);

  const handleMultiSelect = (setter) => (event) => {
    setter(event.target.value);
  };

  const isChecked = (selected, option) =>
    selected.some((item) => item.id === option.id);

  const handleClose = () => {
    setOwnershipEnabled(false);
    setFacilityEnabled(false);
    setQseRoleEnabled(false);
    setOwnershipContacts([]);
    setFacilityContacts([]);
    if(payload?.onClose) payload.onClose();
    setLoading(false)
    setOpen(false)
  };

  const handleConfirm = async () => {
    setLoading(true)
    if (!ownershipContacts.length && !facilityContacts.length && !qseRoleEnabled) return;
    if(payload?.onConfirm) {
      await payload?.onConfirm(
        subject,
        {
          anyQseRole: qseRoleEnabled,
          ownershipContactTypes: ownershipContacts.map((o) => o.id),
          facilityContactTypes: facilityContacts.map((f) => f.id)
        }
      );
    }
    handleClose();
  };

  const canShare =
    ownershipContacts.length || facilityContacts.length || qseRoleEnabled;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Share Article</DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ mb: 3 }} color="text.secondary">
          This will notify all selected users that a new article has
          been published.
        </Typography>

        {/* Email subject input */}
        <TextField
          label="Email subject"
          placeholder="Enter the email subject"
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 3 }}
          helperText="This subject will appear in the notification email."
        />

        <Typography sx={{ mb: 1 }} color="text.secondary">
          <strong>Send to everyone with the following Roles/Contact Types:</strong>
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={qseRoleEnabled}
                onChange={(e) =>
                  setQseRoleEnabled(e.target.checked)
                }
              />
            }
            label="Any QSE role"
          />
        </Box>

        {/* OWNERSHIP */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ownershipEnabled}
                onChange={(e) =>
                  setOwnershipEnabled(e.target.checked)
                }
              />
            }
            label="Ownership Contact Types"
          />

          {ownershipEnabled && (
            <FormControl sx={{ ml: "auto", minWidth: 260 }}>
              <InputLabel>Ownership Contacts</InputLabel>
              <Select
                multiple
                value={ownershipContacts}
                onChange={handleMultiSelect(setOwnershipContacts)}
                label="Ownership Contacts"
                renderValue={(selected) =>
                  selected.map((o) => o.name).join(", ")
                }
              >
                {ownershipContactTypes.map((option) => (
                  <MenuItem key={option.id} value={option}>
                    <Checkbox
                      checked={isChecked(ownershipContacts, option)}
                    />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* FACILITY */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={facilityEnabled}
                onChange={(e) =>
                  setFacilityEnabled(e.target.checked)
                }
              />
            }
            label="Facility Contact Types"
          />

          {facilityEnabled && (
            <FormControl sx={{ ml: "auto", minWidth: 260 }}>
              <InputLabel>Facility Contacts</InputLabel>
              <Select
                multiple
                value={facilityContacts}
                onChange={handleMultiSelect(setFacilityContacts)}
                label="Facility Contacts"
                renderValue={(selected) =>
                  selected.map((f) => f.name).join(", ")
                }
              >
                {facilityContactTypes.map((option) => (
                  <MenuItem key={option.id} value={option}>
                    <Checkbox
                      checked={isChecked(facilityContacts, option)}
                    />
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canShare || loading}
          onClick={handleConfirm}
        >
          Yes, Preview Email
        </Button>
      </DialogActions>
    </Dialog>
  );
}